import { useCallback, useEffect, useRef, useState } from 'react';
import useMarkerBounds from './useMarkerBounds';
import useListeners from './useListeners';
import useMarkers from './useMarkers';

/**
 * Render a Google Map.
 *
 * Example usage:
 *
 *
 * // Any valid Google Map event listeners.
 * // https://developers.google.com/maps/documentation/javascript/events
 * const listeners = {
 *   bounds_changed: () => {},
 * };
 *
 * // Markers to display on the map.
 * const markers = [
 *   {
 *     position: { lat: -25.344, lng: 131.036 },
 *   },
 * ];
 *
 * // Any valid Google Map options.
 * // https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
 * const options = {
 *   zoom: 10,
 * };
 *
 * // Load the Google Maps API.
 * const { googleMaps } = useGoogleMaps('API_KEY');
 *
 * return (
 *   <GoogleMap
 *     api={googleMaps}
 *     clusterMarkers={true}
 *     fitMarkerBounds={true}
 *     listeners={listeners}
 *     markers={markers}
 *     options={options}
 *   />
 * );
 */

type GoogleMapProps = {
  api: {} | any;
  clusterOptions?: object;
  fitMarkerBounds?: boolean;
  listeners?: object;
  options: {
    zoom,
    center?
  },
  markers:object;
  onUpdateMarkers?: (renderedMarkers: object) => void;
  classNames: string;
}

const GoogleMap:React.FC<GoogleMapProps> = ({
  api,
  clusterOptions = null,
  fitMarkerBounds = false,
  listeners = {},
  options = {
    zoom: 10,
    center: null
  },
  markers = [],
  onUpdateMarkers = () => {},
  classNames = '',
}) => {
  const [map, setMap] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const mapEl = useRef();
  const defaultMapOptions = {
    restriction: {
      latLngBounds: {
        north: -34.36,
        south: -47.35,
        west: 166.28,
        east: -175.81,
      },
    },
    disableDefaultUI: true,
    styles: [
      {
        featureType: 'poi.business',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'transit',
        elementType: 'labels.icon',
        stylers: [{ visibility: 'off' }],
      },
    ],
  };

  useEffect(() => {
    if (api !== null && !loaded && mapEl.current && !map) {
      setMap(new api.Map(mapEl.current, { ...defaultMapOptions, ...options }));
      setLoaded(true);
    }

    return () => setMap(null);
  }, [api, mapEl]);

  const renderedMarkers = useMarkers(api, markers, map, clusterOptions);
  const markerBounds: google.maps.LatLngBounds = useMarkerBounds(api, renderedMarkers, fitMarkerBounds);

  onUpdateMarkers(renderedMarkers);

  // When the map bounds changes (user clicks cluster or zooms) we need to manually set the map bounds and center
  // because of a bug with `restriction` in map options.
  useEffect(() => {
    if (markerBounds) {
      try {
        map.fitBounds(markerBounds);
        map.setCenter(markerBounds.getCenter());
      } catch (e) {
        //
      }
    }
  }, [markerBounds]);

  // Pass some useful data back to map event listeners.
  const callbackWrapper = useCallback(
    (eventHandler) => eventHandler({ map, renderedMarkers, markerBounds }),
    [map, renderedMarkers, markerBounds]
  );

  // Register map event listeners.
  useListeners(listeners, map, callbackWrapper);

  return <div ref={mapEl} id="map" className={classNames} />;
};

export default GoogleMap;
